import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '@/components/DashboardLayout';
import { useAuth } from '@/hooks/useAuth';
import axios from '@/utils/api';
import { API } from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, Copy, LinkSimple, Plus } from '@phosphor-icons/react';
import { ContentDeliverableGrid } from '@/components/ContentDeliverableGrid';

export default function CampaignDetail() {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [landingPage, setLandingPage] = useState('');
  const [campaignParam, setCampaignParam] = useState('');
  const [generatedLink, setGeneratedLink] = useState('');
  const [savingLink, setSavingLink] = useState(false);
  const [articleUrl, setArticleUrl] = useState('');
  const [addingArticle, setAddingArticle] = useState(false);

  const fetchCampaign = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/campaigns/${campaignId}`);
      setCampaign(response.data);
      // Pre-fill landing page from brand's base_url if admin
      if (response.data.brand_info?.base_url) {
        setLandingPage(response.data.brand_info.base_url);
      }
      // Show existing tracking link if saved
      if (response.data.tracking_link) {
        setGeneratedLink(response.data.tracking_link);
      }
    } catch (error) {
      toast.error('Failed to load campaign details');
      navigate(-1);
    } finally {
      setLoading(false);
    }
  }, [campaignId, navigate]);

  useEffect(() => {
    fetchCampaign();
  }, [fetchCampaign]);

  if (loading) {
    return (
      <DashboardLayout role={user?.role}>
        <div className="p-8">Loading...</div>
      </DashboardLayout>
    );
  }

  if (!campaign) {
    return (
      <DashboardLayout role={user?.role}>
        <div className="p-8">Campaign not found</div>
      </DashboardLayout>
    );
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-accent border-foreground';
      case 'completed': return 'bg-muted border-foreground';
      case 'paused': return 'bg-background border-foreground';
      case 'draft': return 'bg-background border-foreground';
      default: return 'border-foreground';
    }
  };

  return (
    <DashboardLayout role={user?.role}>
      <div className="p-8" data-testid="campaign-detail-page">
        <Button
          variant="outline"
          onClick={() => navigate(-1)}
          className="mb-6 border-foreground hover:bg-muted"
          data-testid="back-button"
        >
          <ArrowLeft size={20} className="mr-2" />
          Back
        </Button>

        <div className="border border-foreground p-8 mb-6">
          <div className="flex justify-between items-start mb-6">
            <div>
              <h1 className="font-heading text-4xl font-bold mb-2">{campaign.name}</h1>
              <p className="text-muted-foreground">{campaign.category}</p>
            </div>
            <span className={`px-4 py-2 text-sm font-medium border ${getStatusColor(campaign.status)}`}>
              {campaign.status.toUpperCase()}
            </span>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="font-heading text-xl font-bold mb-4">Campaign Details</h3>
              <div className="space-y-3">
                <div>
                  <div className="text-sm text-muted-foreground">Content Type</div>
                  <div className="font-medium">{campaign.content_type}</div>
                </div>
                <div>
                  <div className="text-sm text-muted-foreground">Target Markets</div>
                  <div className="font-medium">{campaign.target_markets.join(', ')}</div>
                </div>
                <div>
                  <div className="text-sm text-muted-foreground">Start Date</div>
                  <div className="font-medium">
                    {campaign.start_date ? new Date(campaign.start_date).toLocaleDateString() : 'Not set'}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-muted-foreground">End Date</div>
                  <div className="font-medium">
                    {campaign.end_date ? new Date(campaign.end_date).toLocaleDateString() : 'Not set'}
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h3 className="font-heading text-xl font-bold mb-4">Budget Breakdown</h3>
              <div className="space-y-3">
                <div>
                  <div className="text-sm text-muted-foreground">Content Budget</div>
                  <div className="font-mono font-medium">${campaign.content_budget.toLocaleString()}</div>
                </div>
                <div>
                  <div className="text-sm text-muted-foreground">Distribution Budget</div>
                  <div className="font-mono font-medium">${campaign.distribution_budget.toLocaleString()}</div>
                </div>
                <div className="pt-3 border-t border-border">
                  <div className="text-sm text-muted-foreground">Total Budget</div>
                  <div className="font-mono font-bold text-2xl">
                    ${(campaign.content_budget + campaign.distribution_budget).toLocaleString()}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {campaign.estimated_outcomes && (
          <div className="border border-foreground p-8 mb-6" data-testid="estimated-outcomes-section">
            <h2 className="font-heading text-2xl font-bold mb-6">Estimated Outcomes</h2>
            <div className="grid md:grid-cols-5 gap-6">
              <div>
                <div className="text-sm text-muted-foreground mb-2">Traffic</div>
                <div className="font-mono text-2xl font-bold">{campaign.estimated_outcomes.traffic.toLocaleString()}</div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground mb-2">Clicks</div>
                <div className="font-mono text-2xl font-bold">{campaign.estimated_outcomes.clicks.toLocaleString()}</div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground mb-2">Orders</div>
                <div className="font-mono text-2xl font-bold">{campaign.estimated_outcomes.orders.toLocaleString()}</div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground mb-2">GMV</div>
                <div className="font-mono text-2xl font-bold">${campaign.estimated_outcomes.gmv.toLocaleString()}</div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground mb-2">ROI</div>
                <div className="font-mono text-2xl font-bold">{(campaign.estimated_outcomes.roi * 100).toFixed(1)}%</div>
              </div>
            </div>
          </div>
        )}

        {campaign.performance_data && (
          <div className="border border-foreground p-8 mb-6" data-testid="performance-data-section">
            <h2 className="font-heading text-2xl font-bold mb-6">Actual Performance</h2>
            <div className="grid md:grid-cols-4 gap-6">
              <div>
                <div className="text-sm text-muted-foreground mb-2">Traffic</div>
                <div className="font-mono text-2xl font-bold">
                  {campaign.performance_data.traffic?.toLocaleString() || 0}
                </div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground mb-2">Clicks</div>
                <div className="font-mono text-2xl font-bold">
                  {campaign.performance_data.clicks?.toLocaleString() || 0}
                </div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground mb-2">Orders</div>
                <div className="font-mono text-2xl font-bold">
                  {campaign.performance_data.orders?.toLocaleString() || 0}
                </div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground mb-2">GMV</div>
                <div className="font-mono text-2xl font-bold">
                  ${campaign.performance_data.gmv?.toLocaleString() || 0}
                </div>
              </div>
            </div>
          </div>
        )}

        {campaign.commerce_links.length > 0 && (
          <div className="border border-foreground p-8">
            <h2 className="font-heading text-2xl font-bold mb-4">Commerce Links</h2>
            <div className="space-y-2">
              {campaign.commerce_links.map((link) => (
                <a
                  key={link}
                  href={link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block text-primary hover:underline"
                >
                  {link}
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Content Deliverables — admin can add/remove; brand sees read-only */}
        {(user?.role === 'admin' && campaign.status === 'active') && (
          <div className="border border-foreground p-8 mt-6" data-testid="content-deliverables-section">
            <h2 className="font-heading text-2xl font-bold mb-6">Content Deliverables</h2>
            <div className="flex gap-3 mb-6">
              <Input
                value={articleUrl}
                onChange={(e) => setArticleUrl(e.target.value)}
                placeholder="Add Article URL"
                className="flex-1"
                data-testid="add-article-url-input"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    document.querySelector('[data-testid="add-article-btn"]')?.click();
                  }
                }}
              />
              <Button
                onClick={async () => {
                  if (!articleUrl.trim()) return;
                  setAddingArticle(true);
                  try {
                    const res = await axios.post(`${API}/admin/campaigns/${campaignId}/deliverables`, { url: articleUrl.trim() });
                    setCampaign(prev => ({
                      ...prev,
                      content_deliverables: [...(prev.content_deliverables || []), res.data]
                    }));
                    setArticleUrl('');
                    toast.success('Article added');
                  } catch (error) {
                    toast.error(error.response?.data?.detail || 'Failed to add article');
                  } finally {
                    setAddingArticle(false);
                  }
                }}
                disabled={addingArticle || !articleUrl.trim()}
                className="bg-primary text-primary-foreground hover:bg-primary/90"
                data-testid="add-article-btn"
              >
                <Plus size={16} className="mr-1" />
                {addingArticle ? 'Adding...' : 'Add'}
              </Button>
            </div>
            <ContentDeliverableGrid
              deliverables={campaign.content_deliverables || []}
              onRemove={async (deliverableId) => {
                try {
                  await axios.delete(`${API}/admin/campaigns/${campaignId}/deliverables/${deliverableId}`);
                  setCampaign(prev => ({
                    ...prev,
                    content_deliverables: (prev.content_deliverables || []).filter(d => d.id !== deliverableId)
                  }));
                  toast.success('Article removed');
                } catch {
                  toast.error('Failed to remove article');
                }
              }}
            />
          </div>
        )}

        {/* Content Delivered — brand read-only view */}
        {user?.role === 'brand' && campaign.content_deliverables?.length > 0 && (
          <div className="border border-foreground p-8 mt-6" data-testid="content-delivered-section">
            <h2 className="font-heading text-2xl font-bold mb-6">Content Delivered</h2>
            <ContentDeliverableGrid deliverables={campaign.content_deliverables} />
          </div>
        )}

        {/* AEO Guidance — visible to publisher (read-only) and admin */}
        {campaign.aeo_brief && (user?.role === 'publisher' || user?.role === 'admin') && (
          <div className="border border-foreground p-8 mt-6" data-testid="aeo-guidance-section">
            <h2 className="font-heading text-2xl font-bold mb-6">
              {user?.role === 'publisher' ? 'AEO Content Guidance' : 'AEO Brief'}
            </h2>
            {user?.role === 'publisher' && (
              <p className="text-sm text-muted-foreground mb-6">
                Use this guidance to structure your content so it gets cited by AI engines like ChatGPT, Gemini and Perplexity.
              </p>
            )}
            <div className="grid md:grid-cols-2 gap-6">
              {campaign.aeo_brief.primary_query && (
                <div data-testid="aeo-field-primary-query">
                  <div className="text-sm font-medium text-muted-foreground mb-1">Primary Query</div>
                  <div className="font-medium">{campaign.aeo_brief.primary_query}</div>
                </div>
              )}
              {campaign.aeo_brief.search_intent_type && (
                <div data-testid="aeo-field-search-intent">
                  <div className="text-sm font-medium text-muted-foreground mb-1">Search Intent Type</div>
                  <div className="font-medium">{campaign.aeo_brief.search_intent_type}</div>
                </div>
              )}
              {campaign.aeo_brief.secondary_queries?.length > 0 && (
                <div className="md:col-span-2" data-testid="aeo-field-secondary-queries">
                  <div className="text-sm font-medium text-muted-foreground mb-1">Secondary Queries</div>
                  <ul className="list-disc list-inside space-y-1">
                    {campaign.aeo_brief.secondary_queries.map((q) => (
                      <li key={q} className="text-sm">{q}</li>
                    ))}
                  </ul>
                </div>
              )}
              {campaign.aeo_brief.content_structure_type && (
                <div data-testid="aeo-field-content-structure">
                  <div className="text-sm font-medium text-muted-foreground mb-1">Content Structure Type</div>
                  <div className="font-medium">{campaign.aeo_brief.content_structure_type}</div>
                </div>
              )}
              {campaign.aeo_brief.brand_placement_type && (
                <div data-testid="aeo-field-brand-placement">
                  <div className="text-sm font-medium text-muted-foreground mb-1">Brand Placement Type</div>
                  <div className="font-medium">{campaign.aeo_brief.brand_placement_type}</div>
                </div>
              )}
              {campaign.aeo_brief.key_brand_narratives && (
                <div className="md:col-span-2" data-testid="aeo-field-narratives">
                  <div className="text-sm font-medium text-muted-foreground mb-1">Key Brand Narratives</div>
                  <div className="text-sm">{campaign.aeo_brief.key_brand_narratives}</div>
                </div>
              )}
              {campaign.aeo_brief.priority_use_cases && (
                <div className="md:col-span-2" data-testid="aeo-field-use-cases">
                  <div className="text-sm font-medium text-muted-foreground mb-1">Priority Use Cases</div>
                  <div className="text-sm">{campaign.aeo_brief.priority_use_cases}</div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Create Tracking Link — admin only, active campaigns only */}
        {user?.role === 'admin' && campaign.status === 'active' && campaign.brand_info && (
          <div className="mt-6 rounded-lg border border-[#2563eb]/30 bg-white p-8 shadow-sm" data-testid="tracking-link-panel">
            <div className="flex items-center gap-2 mb-6">
              <LinkSimple size={22} className="text-[#2563eb]" />
              <h2 className="font-heading text-2xl font-bold">Create Tracking Link</h2>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <Label className="text-sm text-muted-foreground">Brand</Label>
                <Input
                  value={campaign.brand_info.name}
                  readOnly
                  className="mt-1 bg-muted/50 cursor-not-allowed"
                  data-testid="tracking-brand-field"
                />
              </div>
              <div>
                <Label className="text-sm text-muted-foreground">Landing Page</Label>
                <Input
                  value={landingPage}
                  onChange={(e) => setLandingPage(e.target.value)}
                  placeholder="https://example.com/page"
                  className="mt-1"
                  data-testid="tracking-landing-page"
                />
              </div>
              <div className="md:col-span-2">
                <Label className="text-sm text-muted-foreground">Campaign Parameter</Label>
                <Input
                  value={campaignParam}
                  onChange={(e) => setCampaignParam(e.target.value)}
                  placeholder="e.g. spring_sale"
                  className="mt-1"
                  data-testid="tracking-campaign-param"
                />
              </div>
            </div>

            <Button
              onClick={() => {
                const bi = campaign.brand_info;
                const base = landingPage || bi.base_url || '';
                const src = bi.utm_source || '';
                const med = bi.utm_medium || '';
                const prefix = bi.campaign_prefix || '';
                const full = `${base}?utm_source=${encodeURIComponent(src)}&utm_medium=${encodeURIComponent(med)}&utm_campaign=${encodeURIComponent(prefix + campaignParam)}`;
                setGeneratedLink(full);
                // Save to backend
                setSavingLink(true);
                axios.put(`${API}/campaigns/${campaignId}/tracking-link`, { tracking_link: full })
                  .then(() => toast.success('Tracking link created & saved'))
                  .catch(() => toast.error('Failed to save tracking link'))
                  .finally(() => setSavingLink(false));
              }}
              disabled={savingLink}
              className="bg-[#2563eb] text-white hover:bg-[#1d4ed8]"
              data-testid="create-tracking-link-btn"
            >
              {savingLink ? 'Creating...' : 'Create'}
            </Button>

            {generatedLink && (
              <div className="mt-6 flex items-center gap-2" data-testid="generated-link-container">
                <Input
                  value={generatedLink}
                  readOnly
                  className="flex-1 bg-muted/50 font-mono text-sm cursor-text"
                  data-testid="generated-link-field"
                />
                <Button
                  variant="outline"
                  onClick={() => {
                    navigator.clipboard.writeText(generatedLink);
                    toast.success('Link copied to clipboard');
                  }}
                  className="border-[#2563eb] text-[#2563eb] hover:bg-[#2563eb]/10 shrink-0"
                  data-testid="copy-tracking-link-btn"
                >
                  <Copy size={16} className="mr-1" />
                  Copy
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
